import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";

/**
 * Returns the username claimed by the **active Persona** (primary profile),
 * or null if not yet claimed for that Persona.
 *
 * Backed by TanStack Query: the result is cached per personaId and survives
 * route changes / component remounts. ProtectedRoute can therefore consume
 * this without showing a full-page Loading screen on every navigation —
 * the cached value is returned synchronously.
 */
export function useUsername() {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const { activePersonaId, loading: wsLoading } = useWorkspace();
  const personaId = activePersonaId ?? null;
  const qc = useQueryClient();
  const queryKey = ["username", personaId] as const;

  const enabled = !!userId && !!personaId;

  const { data, isLoading, refetch, isFetched } = useQuery({
    queryKey,
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("username")
        .eq("id", personaId!)
        .maybeSingle();
      if (error) throw error;
      return (data?.username as string | null) ?? null;
    },
  });

  const username = (data ?? null) as string | null;

  // Loading only matters on the very first resolution for this persona —
  // after that the cached value is returned synchronously and `loading`
  // stays false on every subsequent navigation/remount.
  const loading =
    authLoading ||
    (!!userId && !personaId && wsLoading) ||
    (enabled && isLoading && !isFetched);

  const resolvedForPersona = !!personaId && isFetched && !isLoading;

  return {
    username,
    loading,
    resolvedForPersona,
    refresh: async () => {
      await qc.invalidateQueries({ queryKey });
      await refetch();
    },
  };
}
